import { config } from '../config';
import type { GraphUser } from '../types';
import { log } from './logger';

export class GraphError extends Error {
  readonly code:
    | 'unauthorized'
    | 'forbidden'
    | 'not_found'
    | 'network'
    | 'unknown';
  readonly status?: number;
  readonly detail?: string;

  constructor(
    message: string,
    code: GraphError['code'],
    status?: number,
    detail?: string,
  ) {
    super(message);
    this.name = 'GraphError';
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

async function graphFetch(path: string, accessToken: string, init?: RequestInit): Promise<Response> {
  const url = path.startsWith('http') ? path : `${config.graphBase}${path}`;
  try {
    return await fetch(url, {
      ...init,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
        ...(init?.headers || {}),
      },
    });
  } catch (e) {
    throw new GraphError('Netzwerkfehler bei Microsoft Graph.', 'network', undefined, String(e));
  }
}

async function readError(res: Response): Promise<string> {
  try {
    const text = await res.text();
    return text.slice(0, 500);
  } catch {
    return res.statusText;
  }
}

function mapHttpError(res: Response, detail: string): GraphError {
  if (res.status === 401) {
    return new GraphError(
      'Graph-Token ungültig oder abgelaufen. Bitte Task Pane neu laden.',
      'unauthorized',
      401,
      detail,
    );
  }
  if (res.status === 403) {
    return new GraphError(
      'Graph-Zugriff verweigert. API-Permission (User.Read / Files.ReadWrite) und Admin Consent prüfen.',
      'forbidden',
      403,
      detail,
    );
  }
  if (res.status === 404) {
    return new GraphError('Ressource nicht gefunden.', 'not_found', 404, detail);
  }
  return new GraphError(`Microsoft Graph Fehler (${res.status}).`, 'unknown', res.status, detail);
}

export async function getMe(accessToken: string): Promise<GraphUser> {
  const res = await graphFetch('/me?$select=displayName,mail,userPrincipalName', accessToken);
  if (!res.ok) {
    throw mapHttpError(res, await readError(res));
  }
  const data = (await res.json()) as {
    displayName?: string;
    mail?: string | null;
    userPrincipalName?: string;
  };
  const user: GraphUser = {
    displayName: data.displayName || '—',
    mail: data.mail || data.userPrincipalName || '—',
    userPrincipalName: data.userPrincipalName || data.mail || '—',
  };
  log('Graph /me erfolgreich', {
    displayName: user.displayName,
    upn: user.userPrincipalName,
  });
  return user;
}

export async function getDrive(accessToken: string): Promise<{ id: string; driveType?: string }> {
  const res = await graphFetch('/me/drive', accessToken);
  if (!res.ok) {
    const detail = await readError(res);
    if (res.status === 404) {
      throw new GraphError(
        'OneDrive ist für diesen Benutzer nicht provisioniert oder nicht verfügbar.',
        'not_found',
        404,
        detail,
      );
    }
    throw mapHttpError(res, detail);
  }
  const data = (await res.json()) as { id: string; driveType?: string };
  log('OneDrive erfolgreich', { id: data.id, driveType: data.driveType });
  return data;
}

export { graphFetch, mapHttpError, readError };
